import { useEffect, useState } from "react";

// Lightweight wrapper around `useState` that keeps a stringly-typed tab
// selection in sync with `window.localStorage`. Mirrors the persistence
// pattern in `src/i18n/i18n.ts` — storage errors (private mode, quota,
// disabled storage) are swallowed so the UI keeps working with the
// in-memory value.
//
// Writes happen in an effect rather than inside the setter so that
// render-time state adjustments (the React "you-might-not-need-an-effect"
// pattern used in `PlaylistTabs`) do not perform side effects during
// render.

export function usePersistentTab<T extends string>(
  key: string,
  fallback: T,
  isValid: (value: string) => value is T,
): readonly [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => readPersistedValue(key, fallback, isValid));

  useEffect(() => {
    writePersistedValue(key, value);
  }, [key, value]);

  return [value, setValue] as const;
}

function readPersistedValue<T extends string>(
  key: string,
  fallback: T,
  isValid: (value: string) => value is T,
): T {
  /* v8 ignore next -- this frontend module normally runs with a browser window. */
  if (typeof window === "undefined") return fallback;
  try {
    const stored = window.localStorage.getItem(key);
    if (stored !== null && isValid(stored)) return stored;
  } catch {
    /* v8 ignore next -- persistence is optional when storage is unavailable. */
  }
  return fallback;
}

function writePersistedValue(key: string, value: string): void {
  /* v8 ignore next -- this frontend module normally runs with a browser window. */
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* v8 ignore next -- persistence is optional when storage is unavailable. */
  }
}
